"use client"

import { useEffect, useRef, useState } from "react"
import { usePreferences } from "@/contexts/preferences-context"
import { convertWindSpeed, convertWaveHeight } from "@/lib/unit-conversions"

export interface HourlyForecast {
  time: string
  timestamp?: string
  hour: number
  waveHeight: number
  waveHeightDisplay: string
  swellHeight?: number
  totalWaveHeight?: number
  windSpeed: number
  windSpeedDisplay: string
  windGust?: number
  windDirection: string
  windDirectionDeg: number
  wavePeriod: number
  waveDirection: string
  waveDirectionDeg: number
  temperature: number
  temperatureDisplay: string
  weatherCode: number
  weatherCondition: string
  weatherIcon: string
  rating: number
  quality: string
  qualityColor: string
  tideHeight?: number
  tideTrend?: 'rising' | 'falling' | 'stable'
  kiteQuality?: string
  kiteQualityColor?: string
  kiteRating?: number
}

interface ForecastMapVisualizationProps {
  latitude: number
  longitude: number
  hourlyData: HourlyForecast | null
  spotName?: string
}

/**
 * Interactive satellite map visualization showing wind and swell conditions
 * Updates dynamically when hovering over forecast timeline
 */
export function ForecastMapVisualization({
  latitude,
  longitude,
  hourlyData,
  spotName = "Spot",
}: ForecastMapVisualizationProps) {
  const { preferences } = usePreferences()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const windArrowRef = useRef<any>(null)
  const swellArrowRef = useRef<any>(null)
  const compassRef = useRef<any>(null)
  const spotMarkerRef = useRef<any>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [hasEverHovered, setHasEverHovered] = useState(false) // Track if user has ever hovered over timeline
  const [lastHourlyData, setLastHourlyData] = useState<HourlyForecast | null>(null) // Store last hourly data for legend persistence

  useEffect(() => {
    const loadLeaflet = async () => {
      if (typeof window === "undefined") return

      try {
        // Load Leaflet CSS if not already loaded
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement("link")
          link.rel = "stylesheet"
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
          document.head.appendChild(link)
        }

        // Load Leaflet JS if not already loaded
        if (!(window as any).L) {
          const script = document.createElement("script")
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
          script.onload = () => {
            initializeMap()
          }
          document.head.appendChild(script)
        } else {
          initializeMap()
        }
      } catch (error) {
        console.error("Failed to load Leaflet:", error)
      }
    }

    const initializeMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return

      const L = (window as any).L
      if (!L) return

      // Initialize map centered on the spot
      const map = L.map(mapRef.current, {
        center: [latitude, longitude],
        zoom: 11, // Zoom level for regional view
        zoomControl: false, // Disable zoom controls for compact view
        scrollWheelZoom: false, // Disable scroll zoom for compact view
        doubleClickZoom: false,
        dragging: false, // Disable dragging for compact view
        touchZoom: false,
        boxZoom: false,
        keyboard: false,
      })

      // Use satellite imagery tile layer (Esri World Imagery)
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        attribution: "© Esri",
        maxZoom: 18,
      }).addTo(map)

      // Spot marker removed - compass rose serves as center indicator

      // Wait for map to be fully ready before marking as loaded
      map.whenReady(() => {
        mapInstanceRef.current = map
        setIsMapLoaded(true)
      })
    }

    loadLeaflet()

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [latitude, longitude])

  // Track when user first hovers over timeline - once they do, overlay never shows again
  // Also store last hourlyData for legend persistence
  useEffect(() => {
    if (hourlyData) {
      if (!hasEverHovered) {
        setHasEverHovered(true)
      }
      setLastHourlyData(hourlyData) // Store latest data for legend
    }
  }, [hourlyData, hasEverHovered])

  // Update map visualization when hourly data changes
  useEffect(() => {
    if (!mapInstanceRef.current || !isMapLoaded) {
      // Clear arrows if map not ready
      if (mapInstanceRef.current && windArrowRef.current) {
        mapInstanceRef.current.removeLayer(windArrowRef.current)
        windArrowRef.current = null
      }
      if (mapInstanceRef.current && swellArrowRef.current) {
        mapInstanceRef.current.removeLayer(swellArrowRef.current)
        swellArrowRef.current = null
      }
      return
    }

    // If no hourly data, keep the last arrows visible (don't remove them)
    // This prevents flickering as user moves mouse between timeline elements
    if (!hourlyData) {
      return
    }

    const L = (window as any).L
    if (!L) {
      return
    }

    const map = mapInstanceRef.current

    // Function to create compass (only once)
    const createCompass = () => {
      // Only create if it doesn't exist
      if (compassRef.current) {
        return
      }

      const compassSize = 60
      const compassHtml = `
        <div style="width: ${compassSize}px; height: ${compassSize}px; position: relative; background: white; border: 2px solid rgba(0,0,0,0.3); border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.3); pointer-events: none;">
          <div style="position: relative; width: 100%; height: 100%;">
            <div style="position: absolute; top: 3px; left: 50%; transform: translateX(-50%); color: #dc2626; font-size: 13px; font-weight: 700;">N</div>
            <div style="position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%); color: #1f2937; font-size: 11px; font-weight: 600;">S</div>
            <div style="position: absolute; left: 3px; top: 50%; transform: translateY(-50%); color: #1f2937; font-size: 11px; font-weight: 600;">W</div>
            <div style="position: absolute; right: 3px; top: 50%; transform: translateY(-50%); color: #1f2937; font-size: 11px; font-weight: 600;">E</div>
            <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 2px; height: 2px; background: #1f2937; border-radius: 50%;"></div>
          </div>
        </div>
      `
      
      try {
        const compassMarker = L.marker([latitude, longitude], {
          icon: L.divIcon({
            className: "leaflet-div-icon",
            html: compassHtml,
            iconSize: [compassSize, compassSize],
            iconAnchor: [compassSize / 2, compassSize / 2],
          }),
          zIndexOffset: 100, // Below arrows
        }).addTo(map)
        compassRef.current = compassMarker
      } catch (e) {
        console.error("[ForecastMap] Error adding compass:", e)
      }
    }

    // Function to add arrows to the map
    const addArrows = () => {
      // Remove existing arrows only (compass persists)
      if (windArrowRef.current) {
        map.removeLayer(windArrowRef.current)
      }
      if (swellArrowRef.current) {
        map.removeLayer(swellArrowRef.current)
      }
      
      // Create compass if it doesn't exist
      createCompass()

      // Windrose dimensions - must match compassSize in createCompass
      const compassSize = 60
      const windroseRadius = compassSize / 2 // 30px - radius of the windrose

      // Calculate arrow properties
      const windSpeed = hourlyData.windSpeed || 0
      const windDirectionDeg = hourlyData.windDirectionDeg || 0
      const waveHeight = hourlyData.waveHeight || 0
      const waveDirectionDeg = hourlyData.waveDirectionDeg || 0
      const wavePeriod = hourlyData.wavePeriod || 0

      // Wind arrow: points in direction wind is blowing
      // Use green color to match graph colors
      const windColor = "#10b981" // Green - matches Epic/Good quality color
      
      // Calculate rotation angle: wind direction is where wind comes FROM
      // Arrow tip is at center, body extends outward TOWARD the direction wind is coming FROM
      // Arrow tip is at bottom center (50% 100%), so we need to add 180° to point toward wind origin
      const windArrowRotation = (windDirectionDeg + 180) % 360
      
      // Wind arrow: single unified arrow shape extending from center outward
      // Arrow length scales with wind speed (stronger = longer)
      // Base length: 60px, max length: 180px, scales with wind speed (0-50+ mph)
      const arrowContainerSize = 300 // Larger container for longer arrows
      const minWindArrowLength = 60 // Minimum arrow length (weak wind)
      const maxWindArrowLength = 180 // Maximum arrow length (strong wind)
      const maxWindSpeed = 50 // Maximum wind speed for scaling (mph)
      const windArrowLength = Math.min(
        minWindArrowLength + ((windSpeed / maxWindSpeed) * (maxWindArrowLength - minWindArrowLength)),
        maxWindArrowLength
      )
      const windArrowBodyLength = Math.round(windArrowLength * 0.75) // 75% body, 25% head
      const windArrowHeadSize = windArrowLength - windArrowBodyLength // Remaining is head
      const windArrowHeight = 35 // Height of arrow body
      const windText = `${hourlyData.windSpeedDisplay || '0'} ${hourlyData.windDirection || ''}`
      
      // Single arrow: tip at windrose edge, body extends outward
      // Arrow rotates around compass center, but tip starts at windrose radius (30px from center)
      const windArrowHtml = `
        <div style="width: ${arrowContainerSize}px; height: ${arrowContainerSize}px; position: relative; background: transparent; pointer-events: none;">
          <!-- Rotating arrow container - rotates around center (150, 150) -->
          <div style="width: ${arrowContainerSize}px; height: ${arrowContainerSize}px; position: absolute; left: 0; top: 0; transform: rotate(${windArrowRotation}deg); transform-origin: ${arrowContainerSize/2}px ${arrowContainerSize/2}px; background: transparent; pointer-events: none;">
            <!-- Arrow div positioned so its tip is at windrose radius distance from center -->
            <!-- Tip should be at (150, 150 - windroseRadius) = (150, 120) in unrotated state -->
            <div style="position: absolute; left: ${arrowContainerSize/2 - windArrowHeight/2}px; top: ${arrowContainerSize/2 - windArrowLength - windroseRadius}px; width: ${windArrowHeight}px; height: ${windArrowLength}px; background: ${windColor}; border: none; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.5); clip-path: polygon(0% 0%, 100% 0%, 100% ${(windArrowBodyLength / windArrowLength) * 100}%, 50% 100%, 0% ${(windArrowBodyLength / windArrowLength) * 100}%);">
            </div>
          </div>
        </div>
      `
      
      // Swell arrow: points in direction swell is traveling (toward shore)
      // Use blue color to match wave graph colors
      const swellColor = "#2563eb" // Blue - matches wave graph stroke color
      
      // Calculate rotation angle: swell direction is where swell comes FROM
      // Arrow tip is at center, body extends outward TOWARD the direction swell is coming FROM
      // Arrow tip is at bottom center (50% 100%), so we need to add 180° to point toward swell origin
      const swellArrowRotation = (waveDirectionDeg + 180) % 360
      
      // Swell arrow: single unified arrow shape extending from center outward
      // Arrow length scales with wave height (higher = longer)
      // Base length: 60px, max length: 180px, scales with wave height (0-8+ ft)
      const minSwellArrowLength = 60 // Minimum arrow length (small waves)
      const maxSwellArrowLength = 180 // Maximum arrow length (large waves)
      const maxWaveHeight = 8 // Maximum wave height for scaling (ft)
      const swellArrowLength = Math.min(
        minSwellArrowLength + ((waveHeight / maxWaveHeight) * (maxSwellArrowLength - minSwellArrowLength)),
        maxSwellArrowLength
      )
      const swellArrowBodyLength = Math.round(swellArrowLength * 0.75) // 75% body, 25% head
      const swellArrowHeadSize = swellArrowLength - swellArrowBodyLength // Remaining is head
      const swellArrowHeight = 35 // Height of arrow body
      const swellText = `${hourlyData.waveHeightDisplay || '0'} ${wavePeriod.toFixed(1)}s`
      
      // Single arrow: tip at windrose edge, body extends outward
      // Arrow rotates around compass center, but tip starts at windrose radius (30px from center)
      const swellArrowHtml = `
        <div style="width: ${arrowContainerSize}px; height: ${arrowContainerSize}px; position: relative; background: transparent; pointer-events: none;">
          <!-- Rotating arrow container - rotates around center (150, 150) -->
          <div style="width: ${arrowContainerSize}px; height: ${arrowContainerSize}px; position: absolute; left: 0; top: 0; transform: rotate(${swellArrowRotation}deg); transform-origin: ${arrowContainerSize/2}px ${arrowContainerSize/2}px; background: transparent; pointer-events: none;">
            <!-- Arrow div positioned so its tip is at windrose radius distance from center -->
            <!-- Tip should be at (150, 150 - windroseRadius) = (150, 120) in unrotated state -->
            <div style="position: absolute; left: ${arrowContainerSize/2 - swellArrowHeight/2}px; top: ${arrowContainerSize/2 - swellArrowLength - windroseRadius}px; width: ${swellArrowHeight}px; height: ${swellArrowLength}px; background: ${swellColor}; border: none; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.5); clip-path: polygon(0% 0%, 100% 0%, 100% ${(swellArrowBodyLength / swellArrowLength) * 100}%, 50% 100%, 0% ${(swellArrowBodyLength / swellArrowLength) * 100}%);">
            </div>
          </div>
        </div>
      `
      
      // Create wind arrow marker at the spot location (above compass)
      // Adjust iconAnchor to ensure rotation center aligns with compass center
      try {
        const windArrowMarker = L.marker([latitude, longitude], {
          icon: L.divIcon({
            className: "leaflet-div-icon",
            html: windArrowHtml,
            iconSize: [arrowContainerSize, arrowContainerSize],
            // iconAnchor must point to the exact pixel where the rotation center is within the icon
            // The rotation center debug marker is at (150, 150) within the 300x300 icon
            // Compass center is at (30, 30) in 60x60 icon, both use iconAnchor at their centers
            // Both should align at the same geographic coordinates
            iconAnchor: [arrowContainerSize / 2, arrowContainerSize / 2],
          }),
          zIndexOffset: 2000, // Above compass
        }).addTo(map)

        windArrowRef.current = windArrowMarker
        map.invalidateSize()
      } catch (e) {
        console.error("[ForecastMap] Error adding wind arrow:", e)
      }
      
      // Create swell arrow marker at spot location (above compass and wind)
      try {
        const swellArrowMarker = L.marker([latitude, longitude], {
          icon: L.divIcon({
            className: "leaflet-div-icon",
            html: swellArrowHtml,
            iconSize: [arrowContainerSize, arrowContainerSize],
            // iconAnchor must point to the exact pixel where the rotation center is within the icon
            // The rotation center debug marker is at (150, 150) within the 300x300 icon
            // Compass center is at (30, 30) in 60x60 icon, both use iconAnchor at their centers
            // Both should align at the same geographic coordinates
            iconAnchor: [arrowContainerSize / 2, arrowContainerSize / 2],
          }),
          zIndexOffset: 2001, // Above compass and wind
        }).addTo(map)

        swellArrowRef.current = swellArrowMarker
        map.invalidateSize()
      } catch (e) {
        console.error("[ForecastMap] Error adding swell arrow:", e)
      }
    }

    // Wait for map to load before adding arrows
    // Since we're using markers instead of polylines, we don't need to wait for viewport initialization
    // Markers don't have the same viewport clipping issues
    const tryAddArrows = () => {
      // Check if map container has dimensions
      const container = map.getContainer()
      if (!container || container.offsetWidth === 0 || container.offsetHeight === 0) {
        return false
      }

      // Ensure map knows its size
      map.invalidateSize()

      // Check if map has valid bounds
      try {
        const bounds = map.getBounds()
        if (bounds && bounds.isValid()) {
          addArrows()
          return true
        }
      } catch (e) {
        console.error("[ForecastMap] Error checking bounds:", e)
      }
      return false
    }

    // Start trying to add arrows
    // First, wait for map load event if it hasn't fired yet
    if (map._loaded) {
      // Map already loaded, try immediately
      if (!tryAddArrows()) {
        // If that didn't work, retry after a short delay
        setTimeout(tryAddArrows, 100)
      }
    } else {
      // Wait for map to load first
      map.once("load", () => {
        // Use a small delay to ensure map is ready
        setTimeout(() => {
          if (!tryAddArrows()) {
            // Last resort: try again after a longer delay
            setTimeout(tryAddArrows, 200)
          }
        }, 50)
      })
    }
  }, [hourlyData, latitude, longitude, isMapLoaded])

  // Inject CSS to ensure arrow markers and compass are visible and transparent
  useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
      .leaflet-div-icon {
        background: transparent !important;
        border: none !important;
      }
      .leaflet-div-icon > div {
        pointer-events: none !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [])

  return (
    <div className="relative w-full h-full bg-slate-900 rounded-lg overflow-hidden border border-slate-200">
        <div ref={mapRef} className="absolute inset-0 z-0" style={{ minHeight: "300px" }} />

      {/* Loading state */}
      {!isMapLoaded && (
        <div className="absolute inset-0 bg-slate-900 flex items-center justify-center z-10">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-sm">Loading map...</p>
          </div>
        </div>
      )}

      {/* Legend - show once user has hovered, persist with last data */}
      {hasEverHovered && isMapLoaded && (hourlyData || lastHourlyData) && (
        <div className="absolute top-2 left-2 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg p-2 shadow-lg z-20 text-xs border border-slate-200 dark:border-slate-700">
          {(() => {
            const dataToShow = hourlyData || lastHourlyData
            if (!dataToShow) return null
            
            // Convert units based on user preferences
            // API returns values like "29 mph" and "1 ft", convert them
            const windSpeedDisplay = dataToShow.windSpeedDisplay 
              ? convertWindSpeed(dataToShow.windSpeedDisplay, preferences.unitSystem)
              : '0'
            const waveHeightDisplay = dataToShow.waveHeightDisplay
              ? convertWaveHeight(dataToShow.waveHeightDisplay, preferences.unitSystem)
              : '0'
            
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-0.5"
                    style={{ backgroundColor: "#10b981" }}
                  />
                  <span className="text-xs font-medium text-slate-900 dark:text-slate-100">Wind</span>
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    {windSpeedDisplay} {dataToShow.windDirection || ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-0.5"
                    style={{ backgroundColor: "#2563eb" }}
                  />
                  <span className="text-xs font-medium text-slate-900 dark:text-slate-100">Swell</span>
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    {waveHeightDisplay} {dataToShow.waveDirection || ''}
                  </span>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* No data message - only show before user has ever hovered over timeline */}
      {!hasEverHovered && !hourlyData && isMapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-900/50 pointer-events-none">
          <p className="text-white text-sm">Hover over timeline to see conditions</p>
        </div>
      )}
    </div>
  )
}

/**
 * Get color for wind speed visualization
 * Blue (light) → Yellow (moderate) → Red (strong)
 */
function getWindColor(windSpeed: number): string {
  if (windSpeed < 5) return "#3b82f6" // Blue - light
  if (windSpeed < 15) return "#60a5fa" // Light blue
  if (windSpeed < 25) return "#fbbf24" // Yellow - moderate
  if (windSpeed < 35) return "#f97316" // Orange - strong
  return "#ef4444" // Red - very strong
}

/**
 * Get color for swell/wave height visualization
 * Blue (small) → Green (moderate) → Orange (large) → Red (very large)
 */
function getSwellColor(waveHeight: number): string {
  if (waveHeight < 2) return "#3b82f6" // Blue - small
  if (waveHeight < 4) return "#10b981" // Green - moderate
  if (waveHeight < 6) return "#f59e0b" // Orange - large
  return "#ef4444" // Red - very large
}




